import { Uploader, UploadQueueEvent } from '@wix/media-manager-sdk';
import {
  all,
  always,
  assocPath,
  cond,
  equals,
  forEach,
  head,
  omit,
  path,
  pathEq,
  pathOr,
  propOr,
  startsWith,
  T,
} from 'ramda';
import { isArray } from 'ramda-adjunct';
import { useEffect } from 'react';
import { IBiAddMediaFileInfo } from '../../../constants/Bi';
import { MainSource } from '../../../constants/Source';
import { UploadError } from '../../../constants/UploadError';
import { uploadFinishedNotification } from '../../../containers/FilesManager/notifications';
import { now } from '../../env/performance';
import { IWithFolderPathInner } from '../../hoc/withFolderPath/withFolderPath.types';
import {
  MediaManagerSDKFile,
  MediaManagerSDKTotalProgress,
  MediaManagerSDKUploadingFile,
} from '../../media-manager-sdk';
import {
  areSomeMediaTypesUnselected,
  getErrorInfo,
  getFailedFiles,
  getNetworkErrorResumableFiles,
  getSeverErrorAutomaticResumeFiles,
  getItemFromServerResponse,
  getNotAbortedFiles,
  isFileAborted,
  isFileFailed,
  isFileInProgress,
  isFileProcessing,
  isFileUploaded,
  isFileWaiting,
} from '../../uploadingFile';
import { UploadStatus } from '../useFilesUploader/types';
import { useNotificatorConsumer } from '../useNotificator';
import { useTrackAddMediaUploadProcessAnalytics } from './useTrackAddMediaUploadProcessAnalytics';
import { captureException } from '@sentry/react';

// list of files for which we tracked `addMedia.waiting.completed` event (to not track it twice).
const waitingCompletedTrackedForFiles = new Set<object>();
// list of files for which we tracked `addMedia.uploading.completed` event (to not track it twice).
// Unfortunately media-manager-sdk emits `fileStatusChanged` event more then once per actual status change
const uploadingCompletedTrackedForFiles = new Set<object>();

interface IParams {
  openFolderById: IWithFolderPathInner['openFolderById'];
  mediaType: string;
  selectAllMediaTypes: () => void;
}

export const useTrackUploadQueue = ({ openFolderById, mediaType, selectAllMediaTypes }: IParams) => {
  const {
    trackAddMediaStarted,
    trackAddMediaFinished,
    trackAddMediaWaitingCompleted,
    trackAddMediaUploadingCompleted,
  } = useTrackAddMediaUploadProcessAnalytics();

  const { notify } = useNotificatorConsumer();

  useEffect(() => {
    const handleUploadQueueStarted = (queue: MediaManagerSDKUploadingFile[]) => {
      forEach<MediaManagerSDKUploadingFile, MediaManagerSDKUploadingFile[]>((item) => {
        if (isFileWaiting(item)) {
          uploadingCompletedTrackedForFiles.delete(path(['source', 'file'], item));
          /**
           * In case when we import file from external source we already know it's mediaType.
           * In case when user uploads file from his computer we need take mediaType from `item.source.mediaType`
           * which is calculated based on files mime-type
           */
          if (!path(['options', 'bi', 'mediaType'], item)) {
            item.options = assocPath(['bi', 'mediaType'], item.source.mediaType, item.options);
          }

          // omit `startedTime` because we don't want to send it to bi logging server. It can be already there is case user retries to upload file.
          // omit `isTags` because it's needed only in `addMediaFinished` event.
          trackAddMediaStarted({
            ...omit(['startedTime', 'isTags'], item.options.bi),
            fileSize: item.getFileSize(),
            uploadType: item.bi.uploadType,
          });

          // `now()` will be used later to calculate loading time
          item.options = assocPath(['bi', 'startedTime'], now(), item.options);
        }
      }, queue);
    };

    const handleUploadFileStatusChange = (progress: MediaManagerSDKTotalProgress) => {
      const file = progress.currentFileProgress.file;
      const sourceFile = path<object>(['source', 'file'], file);
      const biInfo: Partial<IBiAddMediaFileInfo> = pathOr({}, ['options', 'bi'], file);
      const { isTags } = biInfo;
      const biInfoCleaned = omit(['startedTime', 'isTags'], biInfo);
      const loadingTime = Math.round(now() - biInfo.startedTime);

      function trackUploadingCompleted(params) {
        if (uploadingCompletedTrackedForFiles.has(sourceFile)) {
          return;
        }
        uploadingCompletedTrackedForFiles.add(sourceFile);
        trackAddMediaUploadingCompleted({
          ...params,
          errorReason: file.bi.TusErrorReason,
          fileSize: file.getFileSize(),
          manual_resumed: file.bi.manualResumeCount,
          auto_resumed: file.bi.autoResumeCount,
        });
      }

      // `fileStatusChanged` event can be emitted multiple times
      // so we need to check if it's already was tracked for particular file
      if (isFileInProgress(file) && !waitingCompletedTrackedForFiles.has(sourceFile)) {
        waitingCompletedTrackedForFiles.add(sourceFile);
        trackAddMediaWaitingCompleted({
          ...biInfoCleaned,
          loadingTime,
          success: true,
        });
      }

      if (isFileProcessing(file)) {
        trackUploadingCompleted({
          ...biInfoCleaned,
          loadingTime,
          success: true,
        });
      }

      if (isFileUploaded(file)) {
        const itemOrArrayOfItems = getItemFromServerResponse<MediaManagerSDKFile>(file);
        const serverData = isArray(itemOrArrayOfItems) ? head(itemOrArrayOfItems) : itemOrArrayOfItems;

        const { labels: serverAssignedTags, id: fileId, mediaType } = serverData;

        const finishedParams = {
          ...biInfoCleaned,
          is_resumable: file.isTus,
          success: true,
          loadingTime,
          fileId,
          mediaType,
          fileSize: file.getFileSize(),
          manual_resumed: file.bi.manualResumeCount,
          auto_resumed: file.bi.autoResumeCount,
          /**
           * If server has added tags to file then `isTags` will be `true`.
           * Also it will be true when we import from external source and there are already some tags.
           * In other cases `isTags` will be false.
           */
          isTags: serverAssignedTags ? Boolean(serverAssignedTags.length) : isTags,
        };

        waitingCompletedTrackedForFiles.delete(sourceFile);
        trackAddMediaFinished(finishedParams);
      }

      if (isFileFailed(file)) {
        let trackFailed;

        switch (file.failedOnStage) {
          case UploadStatus.Waiting:
            trackFailed = trackAddMediaWaitingCompleted;
            break;
          case UploadStatus.Uploading:
            trackFailed = trackUploadingCompleted;
            break;
          default:
            trackFailed = trackAddMediaFinished;
        }

        captureException(new Error(`[upload] ${file.error.message}`, { cause: file.error }));

        const errorInfo = getErrorInfo(file.error);
        const errorReason =
          path<string>(['error', 'data', 'error_description'], file) ||
          `${path(['error', 'message'], file)} at ${path(['error', 'config', 'url'], file)}`;

        errorInfo.key = cond([
          [equals('wixSession2 cookie not found'), always('wmp_error.session_expired')],
          [equals('mismatch wixSession and siteToken'), always('wmp_error.invalid_session')],
          [equals('invalid siteToken'), always('wmp_error.invalid_session')],
          [startsWith('Network Error'), always('wmp_error.network_general')],
          [startsWith('timeout of 0ms'), always('wmp_error.network_general')],
          [T, always(errorInfo.key)],
        ])(errorReason);

        const biFailInfo = {
          ...biInfoCleaned,
          loadingTime,
          success: false,
          errorReason,
          errorKey: errorInfo.key,
        };

        if (errorInfo.key === UploadError.SizeLimit) {
          trackAddMediaFinished(biFailInfo);
        }

        trackFailed(biFailInfo);

        waitingCompletedTrackedForFiles.delete(sourceFile);
      }

      if (isFileAborted(file)) {
        let trackFailed;

        switch (file.failedOnStage) {
          case UploadStatus.Waiting:
            trackFailed = trackAddMediaWaitingCompleted;
            break;
          case UploadStatus.Uploading:
            trackFailed = trackUploadingCompleted;
            break;
          default:
            trackFailed = trackAddMediaFinished;
        }

        trackFailed({
          ...biInfoCleaned,
          loadingTime,
          success: false,
          errorReason: 'cancelled_by_user',
        });

        waitingCompletedTrackedForFiles.delete(sourceFile);
      }
    };

    const handleUploadQueueFinished = (queue: MediaManagerSDKUploadingFile[]) => {
      const notAbortedFilesCount = getNotAbortedFiles(queue).length;
      const failedFilesCount = getFailedFiles(queue).length;
      const networkErrorResumableFilesCount = getNetworkErrorResumableFiles(queue).length;
      const ServerErrorAutomaticResumeFiles = getSeverErrorAutomaticResumeFiles(queue).length;

      const isConnectivityError = networkErrorResumableFilesCount > 0;
      const isAutomaticResumeServerError = ServerErrorAutomaticResumeFiles > 0;

      if (notAbortedFilesCount) {
        const [firstFileInQueue] = queue;

        const serverResponse = getItemFromServerResponse(firstFileInQueue);

        const fileName = isArray(serverResponse)
          ? propOr(serverResponse[0].name, 'original_file_name', serverResponse[0])
          : serverResponse.name;
        const { folderName } = firstFileInQueue.options;

        // If targets are different then targetName is null
        const targetName = all(pathEq(['options', 'folderName'], folderName), queue) ? folderName : null;
        const redirectToFolderId = firstFileInQueue.source.folderId;

        notify(
          uploadFinishedNotification({
            failedFilesCount,
            filesCount: notAbortedFilesCount,
            fileName,
            targetName,
            isAutomaticResumeServerError,
            isConnectivityError,
            onTargetClick() {
              const hasUnusedMediaTypes = areSomeMediaTypesUnselected(queue, mediaType);
              if (hasUnusedMediaTypes) {
                selectAllMediaTypes();
              }

              openFolderById(
                redirectToFolderId,
                MainSource.SiteFiles,
                null,
                notAbortedFilesCount === 1 ? serverResponse.id : null,
              );
            },
          }),
        );
      }
    };

    Uploader.on(UploadQueueEvent.UploadQueueStarted, handleUploadQueueStarted);
    Uploader.on(UploadQueueEvent.FileStatusChanged, handleUploadFileStatusChange);
    Uploader.on(UploadQueueEvent.UploadQueueFinished, handleUploadQueueFinished);

    return () => {
      Uploader.off(UploadQueueEvent.UploadQueueStarted, handleUploadQueueStarted);
      Uploader.off(UploadQueueEvent.FileStatusChanged, handleUploadFileStatusChange);
      Uploader.off(UploadQueueEvent.UploadQueueFinished, handleUploadQueueFinished);
    };
  }, []);
};
